import { useState, useEffect, useCallback } from 'react';
import Reconciliation from './Reconciliation';

const API = 'https://tcio-api.onrender.com';

// Facilities load from /api/facilities (active, scope-filtered) - zero-hardcode doctrine.

const PAYMENT_METHODS = ['Check', 'EFT', 'Wire', 'Credit Card', 'Cash'];   // Credit Card + Cash (Chad 11/2)

function fmtMoney(n, short = false) {
  if (!n && n !== 0) return '$0';
  const val = parseFloat(n);
  if (short && val >= 1000000) return '$' + (val / 1000000).toFixed(2) + 'M';
  if (short && val >= 1000) return '$' + (val / 1000).toFixed(1) + 'K';
  return '$' + val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtDate(d) {
  if (!d) return '—';
  try {
    const m = (d + '').match(/^(\d{4})-(\d{2})-(\d{2})/);
    const dt = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(d);
    return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }
  catch { return '—'; }
}

function todayStr() { return new Date().toISOString().split('T')[0]; }
function fmtMonthLabel(m) {
  if (!m) return '';
  const [y, mo] = m.split('-');
  const d = new Date(parseInt(y), parseInt(mo) - 1, 1);
  return d.toLocaleString('en-US', { month: 'short', year: 'numeric' });
}
function isToday(d) { return d && (d + '').startsWith(todayStr()); }
function isOverdue(d) { return d && new Date(d) < new Date() && !isToday(d); }

const emptyReceived = {
  date_received: todayStr(),
  facility_id: '',
  customer: '', patient_name: '', amount: '',
  payment_method: 'EFT', check_number: '', notes: '',
};

const emptyPending = {
  facility_id: '',
  payer_name: '', expected_amount: '',
  issue_date: '', expected_date: '',
  payment_method: 'Check', check_number: '', notes: '',
};

const s = {
  page: { padding: '24px', maxWidth: '1400px', margin: '0 auto' },
  pageTitle: { fontSize: '22px', fontWeight: '700', color: '#1e293b', margin: '0 0 4px 0' },
  pageSub: { fontSize: '13px', color: '#64748b', marginBottom: '20px' },

  // Today banner
  todayBanner: { background: 'linear-gradient(135deg, #1e40af, #2563eb)', borderRadius: '12px', padding: '20px 24px', marginBottom: '20px', color: '#fff' },
  todayTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' },
  todayLabel: { fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.08em', opacity: 0.8 },
  todayTotal: { fontSize: '36px', fontWeight: '900', letterSpacing: '-0.5px' },
  todayCount: { fontSize: '13px', opacity: 0.8, marginTop: '2px' },
  todayFacilities: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' },
  todayFacCard: { background: 'rgba(255,255,255,0.12)', borderRadius: '8px', padding: '12px 14px' },
  todayFacName: { fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', opacity: 0.8, marginBottom: '4px' },
  todayFacAmt: { fontSize: '18px', fontWeight: '800' },
  todayFacCount: { fontSize: '11px', opacity: 0.7, marginTop: '2px' },

  // Summary grid
  summaryGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' },
  summaryCard: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', overflow: 'hidden' },
  summaryCardTitle: { padding: '14px 18px', borderBottom: '1px solid #f1f5f9', fontSize: '13px', fontWeight: '700', color: '#1e293b', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  summaryTable: { width: '100%', borderCollapse: 'collapse' },
  summaryTh: { padding: '9px 14px', fontSize: '10px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'right', borderBottom: '1px solid #f1f5f9', background: '#f8fafc' },
  summaryThLeft: { padding: '9px 14px', fontSize: '10px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'left', borderBottom: '1px solid #f1f5f9', background: '#f8fafc' },
  summaryTd: { padding: '9px 14px', fontSize: '13px', color: '#334155', borderBottom: '1px solid #f8fafc', textAlign: 'right' },
  summaryTdLeft: { padding: '9px 14px', fontSize: '13px', color: '#334155', borderBottom: '1px solid #f8fafc', textAlign: 'left', fontWeight: '600' },
  summaryTdGreen: { padding: '9px 14px', fontSize: '13px', fontWeight: '700', color: '#15803d', borderBottom: '1px solid #f8fafc', textAlign: 'right' },
  summaryTdYellow: { padding: '9px 14px', fontSize: '13px', fontWeight: '700', color: '#d97706', borderBottom: '1px solid #f8fafc', textAlign: 'right' },
  totalRow: { background: '#f8fafc' },
  totalTd: { padding: '10px 14px', fontSize: '13px', fontWeight: '800', color: '#1e293b', borderTop: '2px solid #e2e8f0', textAlign: 'right' },
  totalTdLeft: { padding: '10px 14px', fontSize: '13px', fontWeight: '800', color: '#1e293b', borderTop: '2px solid #e2e8f0', textAlign: 'left' },

  // Tabs
  tabRow: { display: 'flex', gap: '4px', marginBottom: '20px', borderBottom: '2px solid #e2e8f0' },
  tab: (active) => ({
    padding: '10px 20px', fontSize: '14px', fontWeight: active ? '700' : '500',
    color: active ? '#2563eb' : '#64748b', background: 'none', border: 'none',
    cursor: 'pointer', borderBottom: active ? '2px solid #2563eb' : '2px solid transparent',
    marginBottom: '-2px'
  }),

  // Form
  formCard: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '20px 24px', marginBottom: '20px' },
  formTitle: { fontSize: '14px', fontWeight: '700', color: '#1e293b', marginBottom: '14px' },
  formGrid4: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '12px' },
  label: { fontSize: '11px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px', display: 'block' },
  input: { width: '100%', padding: '9px 12px', fontSize: '13px', border: '1px solid #cbd5e1', borderRadius: '6px', outline: 'none', boxSizing: 'border-box', color: '#1e293b' },
  select: { width: '100%', padding: '9px 12px', fontSize: '13px', border: '1px solid #cbd5e1', borderRadius: '6px', outline: 'none', boxSizing: 'border-box', color: '#1e293b', background: '#fff' },
  formActions: { display: 'flex', gap: '10px', alignItems: 'center', marginTop: '4px' },
  submitBtn: { padding: '10px 24px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '7px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  submitBtnYellow: { padding: '10px 24px', background: '#d97706', color: '#fff', border: 'none', borderRadius: '7px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  clearBtn: { padding: '10px 18px', background: 'none', color: '#64748b', border: '1px solid #e2e8f0', borderRadius: '7px', cursor: 'pointer', fontSize: '13px' },
  successMsg: { fontSize: '13px', color: '#15803d', fontWeight: '600' },
  errorMsg: { fontSize: '13px', color: '#dc2626', fontWeight: '600' },

  // Table
  tableCard: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', overflow: 'hidden', marginBottom: '20px' },
  tableHeader: { padding: '14px 18px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  tableTitle: { fontSize: '14px', fontWeight: '700', color: '#1e293b' },
  filterRow: { display: 'flex', gap: '10px', alignItems: 'center' },
  searchInput: { padding: '7px 12px', fontSize: '13px', border: '1px solid #e2e8f0', borderRadius: '6px', outline: 'none', width: '180px' },
  filterSelect: { padding: '7px 12px', fontSize: '13px', border: '1px solid #e2e8f0', borderRadius: '6px', outline: 'none', background: '#fff' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { background: '#f8fafc', padding: '9px 14px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #e2e8f0' },
  td: { padding: '9px 14px', fontSize: '13px', color: '#334155', borderBottom: '1px solid #f1f5f9' },
  tdGreen: { padding: '9px 14px', fontSize: '13px', fontWeight: '700', color: '#15803d', borderBottom: '1px solid #f1f5f9', textAlign: 'right' },
  tdYellow: { padding: '9px 14px', fontSize: '13px', fontWeight: '700', color: '#d97706', borderBottom: '1px solid #f1f5f9', textAlign: 'right' },
  actionBtn: { background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', padding: '3px 8px', borderRadius: '4px' },
  todayTag: { display: 'inline-block', padding: '1px 7px', borderRadius: '999px', fontSize: '10px', fontWeight: '700', background: '#dbeafe', color: '#1d4ed8', marginLeft: '6px' },
  overdueTag: { display: 'inline-block', padding: '1px 7px', borderRadius: '999px', fontSize: '10px', fontWeight: '700', background: '#fee2e2', color: '#dc2626', marginLeft: '6px' },
  emptyRow: { padding: '32px', textAlign: 'center', color: '#94a3b8', fontSize: '13px' },
  receiveBtn: { padding: '4px 12px', background: '#dcfce7', color: '#15803d', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' },

  // Modal
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  modal: { background: '#fff', borderRadius: '12px', padding: '28px', width: '500px', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' },
  modalTitle: { fontSize: '16px', fontWeight: '700', color: '#1e293b', marginBottom: '16px' },
  modalGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' },
  modalActions: { display: 'flex', gap: '10px', justifyContent: 'flex-end' },
  saveBtn: { padding: '9px 22px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '7px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' },
  cancelBtn: { padding: '9px 16px', background: 'none', color: '#64748b', border: '1px solid #e2e8f0', borderRadius: '7px', cursor: 'pointer', fontSize: '13px' },
  deleteBtn: { padding: '9px 16px', background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '7px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', marginRight: 'auto' },
};

export default function Collections({ user }) {
  const [activeTab, setActiveTab] = useState('received');
  const [receivedForm, setReceivedForm] = useState({ ...emptyReceived });
  const [pendingForm, setPendingForm] = useState({ ...emptyPending });
  const [submitting, setSubmitting] = useState(false);
  const [submitMsg, setSubmitMsg] = useState(null);
  const [payments, setPayments] = useState([]);
  const [pending, setPending] = useState([]);
  const [editPending, setEditPending] = useState(null);
  const [editPendingForm, setEditPendingForm] = useState({});
  const [editPendingBusy, setEditPendingBusy] = useState(false);
  const savePendingEdit = async () => {
    setEditPendingBusy(true);
    try {
      const r = await fetch(`${API}/api/pending-payments/${editPending.id}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${localStorage.getItem('tcio_token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(editPendingForm),
      });
      const j = await r.json();
      if (j.ok) { setEditPending(null); loadData(); }
      else window.alert(j.error || 'Save failed');
    } catch (e) { window.alert('Network error'); }
    setEditPendingBusy(false);
  };
  const [payerList, setPayerList] = useState([]);
  const [facilitySummary, setFacilitySummary] = useState([]);
  const [monthData, setMonthData] = useState([]);
  const [monthList, setMonthList] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [search, setSearch] = useState('');
  const [filterFacility, setFilterFacility] = useState('all');
  const [editRecord, setEditRecord] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [receiveModal, setReceiveModal] = useState(null);
  const [receiveAmount, setReceiveAmount] = useState('');
  const [receiveDate, setReceiveDate] = useState(todayStr());
  const [loading, setLoading] = useState(true);
  // REMITTANCE UPLOAD (owner and admins): files from the clearinghouse remittance viewer become pending payments.
  const [eob, setEob] = useState({ can_upload: false, uploads: [], links: {}, facilities: [] });
  const [eobBusy, setEobBusy] = useState(false);
  const [eobResults, setEobResults] = useState(null);
  const [eobPick, setEobPick] = useState({});
  // BANK DEPOSITS (mig 318): screenshots of each account's deposits, read and matched to pending payments.
  const [dep, setDep] = useState({ can_upload: false, uploads: [], unmatched: [], strip: [], accounts: [], pending: [], companies: [] });
  const [depBusy, setDepBusy] = useState(false); const [depResults, setDepResults] = useState(null); const [depPick, setDepPick] = useState({});
  const [assign, setAssign] = useState({}); // deposit id -> { pending_payment_id | kind, note, payer_name, accept }
  const [newAcct, setNewAcct] = useState({ organization_id: '', name: '', last4: '', bank_name: '' }); const [showAcct, setShowAcct] = useState(false); const [acctFor, setAcctFor] = useState(null); // the picture waiting for the account being added

  const token = () => localStorage.getItem('tcio_token');
  const [facilities, setFacilities] = useState([]);
  useEffect(() => { (async () => {
    try {
      const r = await fetch(API + '/api/facilities', { headers: { Authorization: 'Bearer ' + token() } });
      const j = await r.json();
      if (j.ok) setFacilities(j.data.map(x => ({ id: x.id, name: x.org_name, short: x.org_name })));
    } catch (e) {}
  })(); }, []);

  const loadData = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${token()}` };
      const [recRes, penRes, facRes, monRes] = await Promise.all([
        fetch(`${API}/api/collections/recent?limit=150`, { headers }),
        fetch(`${API}/api/pending-payments`, { headers }),
        fetch(`${API}/api/collections/facility-summary`, { headers }),
        fetch(`${API}/api/collections/facility-months`, { headers }),
      ]);
      const recData = await recRes.json();
      const penData = await penRes.json();
      const facData = await facRes.json();
      const monData = await monRes.json();
      if (recData.ok) setPayments(recData.data);
      if (penData.ok) setPending(penData.data);
      try { const er = await fetch(`${API}/api/rcm/eobs`, { headers }); const ej = await er.json(); if (ej.ok) setEob(ej); } catch (e) {}
      try { const dr = await fetch(`${API}/api/bank-deposits`, { headers }); const dj = await dr.json(); if (dj.ok) setDep(dj); } catch (e) {}
      try {
        const plRes = await fetch(`${API}/api/collections/payer-list`, { headers: { Authorization: `Bearer ${token()}` } });
        const plData = await plRes.json();
        if (plData.ok) setPayerList(plData.payers);
      } catch (e) {}
      if (facData.ok) setFacilitySummary(facData.data);
      if (monData.ok) {
        setMonthData(monData.data);
        setMonthList(monData.months);
        setSelectedMonth(prev => prev || monData.months[0] || null);
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Submit received payment
  const handleSubmitReceived = async () => {
    if (!receivedForm.facility_id || !receivedForm.date_received || !receivedForm.amount || !receivedForm.customer) {
      setSubmitMsg({ type: 'error', text: 'Date, facility, payer, and amount are required.' });
      return;
    }
    setSubmitting(true);
    setSubmitMsg(null);
    try {
      const res = await fetch(`${API}/api/collections`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({
          facility_id: receivedForm.facility_id,
          date_received: receivedForm.date_received,
          entity: facilities.find(f => f.id === receivedForm.facility_id)?.name || '',
          payment_type_category: 'Insurance Payment',
          customer: receivedForm.customer.trim(),
          amount: parseFloat(receivedForm.amount),
          payment_method: receivedForm.check_number
            ? receivedForm.payment_method + ' ' + receivedForm.check_number.trim()
            : receivedForm.payment_method,
          patient_names: receivedForm.patient_name.trim() || null,
          month_tab: new Date(receivedForm.date_received + 'T12:00:00').toLocaleString('en-US', { month: 'long', year: 'numeric' }),
        }),
      });
      const data = await res.json();
      if (data.ok) {
        setSubmitMsg({ type: 'success', text: `✓ ${fmtMoney(receivedForm.amount)} logged successfully.` });
        setReceivedForm({ ...emptyReceived, date_received: receivedForm.date_received, facility_id: receivedForm.facility_id });
        await loadData();
        setTimeout(() => setSubmitMsg(null), 4000);
      } else {
        setSubmitMsg({ type: 'error', text: data.error || 'Failed to save.' });
      }
    } catch (err) {
      setSubmitMsg({ type: 'error', text: 'Network error.' });
    } finally { setSubmitting(false); }
  };

  // Submit pending
  const handleSubmitPending = async () => {
    if (!pendingForm.facility_id || !pendingForm.payer_name || !pendingForm.expected_amount) {
      setSubmitMsg({ type: 'error', text: 'Facility, payer, and expected amount are required.' });
      return;
    }
    setSubmitting(true);
    setSubmitMsg(null);
    try {
      const res = await fetch(`${API}/api/pending-payments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({
          facility_id: pendingForm.facility_id,
          payer_name: pendingForm.payer_name.trim(),
          expected_amount: parseFloat(pendingForm.expected_amount),
          issue_date: pendingForm.issue_date || null,
          expected_date: pendingForm.expected_date || null,
          check_number: pendingForm.check_number || null,
          payment_method: pendingForm.payment_method,
          notes: pendingForm.notes || null,
        }),
      });
      const data = await res.json();
      if (data.ok) {
        setSubmitMsg({ type: 'success', text: `✓ Pending ${fmtMoney(pendingForm.expected_amount)} logged.` });
        setPendingForm({ ...emptyPending, facility_id: pendingForm.facility_id });
        await loadData();
        setTimeout(() => setSubmitMsg(null), 4000);
      } else {
        setSubmitMsg({ type: 'error', text: data.error || 'Failed to save.' });
      }
    } catch (err) {
      setSubmitMsg({ type: 'error', text: 'Network error.' });
    } finally { setSubmitting(false); }
  };

  // Mark pending received
  const [receiveAccount, setReceiveAccount] = useState('');
  const handleReceive = async () => {
    try {
      const res = await fetch(`${API}/api/pending-payments/${receiveModal.id}/receive`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ date_received: receiveDate, amount: parseFloat(receiveAmount || receiveModal.expected_amount), bank_account_id: receiveAccount || null }),
      });
      const data = await res.json();
      if (data.ok) { setReceiveModal(null); await loadData(); }
    } catch (err) { console.error(err); }
  };

  const handleDeletePending = async (id) => {
    let url = `${API}/api/pending-payments/${id}`;
    if (eob.links && eob.links[id]) {
      const why = window.prompt('This pending payment came from an uploaded remittance. It will be cancelled, not deleted, and the file is kept. Why is it being removed?');
      if (!why || !why.trim()) return;
      url += `?reason=${encodeURIComponent(why.trim())}`;
    } else if (!window.confirm('Remove this pending payment?')) return;
    const dr = await fetch(url, { method: 'DELETE', headers: { Authorization: `Bearer ${token()}` } });
    const dj = await dr.json().catch(() => ({}));
    if (dr.ok === false || dj.ok === false) window.alert(dj.error || 'This pending payment could not be removed. Try once more.');
    await loadData();
  };

  const uploadEobs = async (fileList) => {
    const files = Array.from(fileList || []); if (!files.length) return;
    setEobBusy(true); setEobResults(null);
    try {
      const fd = new FormData(); files.forEach(f => fd.append('files', f));
      const r = await fetch(`${API}/api/rcm/eobs/upload`, { method: 'POST', headers: { Authorization: `Bearer ${token()}` }, body: fd });
      const j = await r.json().catch(() => ({}));
      setEobResults(j.ok ? j.results : [{ filename: '', outcome: 'unreadable', message: j.error || 'The upload did not go through. Check your connection and try once more.' }]);
      await loadData();
    } catch (e) { setEobResults([{ filename: '', outcome: 'unreadable', message: 'The upload did not go through. Check your connection and try once more.' }]); }
    setEobBusy(false);
  };
  const setEobFacility = async (id) => {
    if (!eobPick[id]) { window.alert('Choose the facility first.'); return; }
    setEobBusy(true);
    try {
      const r = await fetch(`${API}/api/rcm/eobs/${id}/facility`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` }, body: JSON.stringify({ facility_id: eobPick[id] }) });
      const j = await r.json().catch(() => ({}));
      if (!j.ok) window.alert(j.error || 'That could not be saved. Try once more.');
      else setEobResults(rs => (rs || []).map(x => x.id === id ? { ...x, outcome: j.outcome, message: j.message } : x));
      await loadData();
    } catch (e) { window.alert('That could not be saved. Check your connection and try once more.'); }
    setEobBusy(false);
  };
  const openEob = async (eobId) => {
    const w = window.open('', '_blank');
    try { const r = await fetch(`${API}/api/rcm/eobs/${eobId}/file`, { headers: { Authorization: `Bearer ${token()}` } }); const j = await r.json(); if (j.ok && j.url) { if (w) w.location = j.url; else window.location = j.url; } else { if (w) w.close(); window.alert(j.error || 'The file could not be opened.'); } }
    catch (e) { if (w) w.close(); window.alert('The file could not be opened. Check your connection and try once more.'); }
  };
  const EOB_TONE = { pending_created: ['#dcfce7', '#15803d', 'Pending payment created'], already_pending: ['#dbeafe', '#1d4ed8', 'Already pending'], already_received: ['#dbeafe', '#1d4ed8', 'Already received'], zero_pay: ['#f1f5f9', '#475569', 'Nothing paid · RCM only'],
    duplicate: ['#f1f5f9', '#475569', 'Already uploaded'], needs_facility: ['#fef3c7', '#b45309', 'Needs a facility'], mismatch: ['#fee2e2', '#dc2626', 'Does not add up'], unreadable: ['#fee2e2', '#dc2626', 'Not read'], cancelled: ['#f1f5f9', '#475569', 'Already uploaded'] };
  const eobWaiting = (eob.uploads || []).filter(u => u.outcome === 'needs_facility' && !(eobResults || []).some(x => x.id === u.id));
  const eobRow = (x, key) => { const [bg, fg, label] = EOB_TONE[x.outcome] || EOB_TONE.unreadable; return (
    <div key={key} style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', padding: '9px 0', borderTop: '1px solid #f1f5f9' }}>
      <span style={{ display: 'inline-block', padding: '2px 9px', borderRadius: '999px', fontSize: '11px', fontWeight: '700', background: bg, color: fg, whiteSpace: 'nowrap' }}>{label}</span>
      <div style={{ flex: '1 1 320px', minWidth: 0, fontSize: '13px', color: '#334155' }}>
        <b>{x.payer || x.payer_printed || x.filename || 'File'}</b>{(x.amount ?? x.payment_amount) != null ? ` · ${fmtMoney(x.amount ?? x.payment_amount)}` : ''}{x.trace_no ? ` · ${x.trace_no}` : ''}{x.check_date ? ` · ${fmtDate(x.check_date)}` : ''}
        <div style={{ fontSize: '12px', color: '#64748b' }}>{x.message || x.problem || ''}{x.filename && (x.payer || x.payer_printed) ? ` (${x.filename})` : ''}</div>
      </div>
      {x.outcome === 'needs_facility' && x.id && (<>
        <select style={{ ...s.filterSelect, minWidth: '180px' }} value={eobPick[x.id] || ''} onChange={e => setEobPick({ ...eobPick, [x.id]: e.target.value })}>
          <option value="">— Which facility? —</option>{(eob.facilities || []).map(f => <option key={f.id} value={f.id}>{f.name}</option>)}</select>
        <button style={{ ...s.receiveBtn, opacity: eobBusy ? 0.6 : 1 }} disabled={eobBusy} onClick={() => setEobFacility(x.id)}>Save</button></>)}
    </div>); };

  const uploadDeposits = async (fileList) => {
    const files = Array.from(fileList || []); if (!files.length) return;
    setDepBusy(true); setDepResults(null);
    try { const fd = new FormData(); files.forEach(f => fd.append('files', f));
      const r = await fetch(`${API}/api/bank-deposits/upload`, { method: 'POST', headers: { Authorization: `Bearer ${token()}` }, body: fd }); const j = await r.json().catch(() => ({}));
      setDepResults(j.ok ? j.results : [{ filename: '', outcome: 'unreadable', message: j.error || 'The upload did not go through. Check your connection and try once more.' }]); await loadData();
    } catch (e) { setDepResults([{ filename: '', outcome: 'unreadable', message: 'The upload did not go through. Check your connection and try once more.' }]); }
    setDepBusy(false); };
  const setDepAccount = async (id) => { if (!depPick[id]) { window.alert('Choose the account first.'); return; } setDepBusy(true);
    try { const r = await fetch(`${API}/api/bank-deposits/uploads/${id}/account`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` }, body: JSON.stringify({ bank_account_id: depPick[id] }) }); const j = await r.json().catch(() => ({}));
      if (!j.ok) window.alert(j.error || 'That could not be saved. Try once more.'); else setDepResults(rs => (rs || []).map(x => x.id === id ? { ...x, ...j } : x)); await loadData(); } catch (e) { window.alert('That could not be saved. Check your connection and try once more.'); }
    setDepBusy(false); };
  const assignDeposit = async (d) => { const a = assign[d.id] || {}; const body = a.pending_payment_id ? { pending_payment_id: a.pending_payment_id, accept_difference: !!a.accept } : { kind: a.kind || '', note: a.note || '', payer_name: a.payer_name || '', facility_id: a.facility_id || '' };
    if (!a.pending_payment_id && !a.kind) { window.alert('Pick a pending payment, or say what this deposit is.'); return; } setDepBusy(true);
    try { const r = await fetch(`${API}/api/bank-deposits/${d.id}/assign`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` }, body: JSON.stringify(body) }); const j = await r.json().catch(() => ({}));
      if (!j.ok) window.alert(j.error || 'That could not be saved. Try once more.'); else { setAssign(s => { const n = { ...s }; delete n[d.id]; return n; }); await loadData(); } } catch (e) { window.alert('That could not be saved. Check your connection and try once more.'); }
    setDepBusy(false); };
  const addAccount = async () => { setDepBusy(true);
    try { const r = await fetch(`${API}/api/bank-deposits/accounts`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` }, body: JSON.stringify(newAcct) }); const j = await r.json().catch(() => ({}));
      if (!j.ok) window.alert(j.error || 'That could not be saved.'); else { const forId = acctFor; setNewAcct({ organization_id: '', name: '', last4: '', bank_name: '' }); setShowAcct(false); setAcctFor(null);
        if (forId && j.id) { const r2 = await fetch(`${API}/api/bank-deposits/uploads/${forId}/account`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` }, body: JSON.stringify({ bank_account_id: j.id }) }); const j2 = await r2.json().catch(() => ({})); if (!j2.ok) window.alert(j2.error || 'The account was added, but the picture could not be assigned to it. Choose it from the list.'); }
        await loadData(); } } catch (e) { window.alert('That could not be saved. Check your connection and try once more.'); }
    setDepBusy(false); };
  const openDepFile = async (id) => { const w = window.open('', '_blank');
    try { const r = await fetch(`${API}/api/bank-deposits/uploads/${id}/file`, { headers: { Authorization: `Bearer ${token()}` } }); const j = await r.json(); if (j.ok && j.url) { if (w) w.location = j.url; else window.location = j.url; } else { if (w) w.close(); window.alert(j.error || 'The picture could not be opened.'); } }
    catch (e) { if (w) w.close(); window.alert('The picture could not be opened. Check your connection and try once more.'); } };
  const DEP_TONE = { posted: ['#dcfce7', '#15803d', 'All posted'], needs_assign: ['#fef3c7', '#b45309', 'Some to assign'], nothing_new: ['#f1f5f9', '#475569', 'Already in'], nothing_in: ['#f1f5f9', '#475569', 'No deposits'], needs_account: ['#fef3c7', '#b45309', 'Which account?'], duplicate: ['#f1f5f9', '#475569', 'Already uploaded'], unreadable: ['#fee2e2', '#dc2626', 'Not read'], reading: ['#f1f5f9', '#475569', 'Reading'] };
  const depWaiting = (dep.uploads || []).filter(x => x.outcome === 'needs_account' && !(depResults || []).some(r => r.id === x.id));
  const depRow = (x, key) => { const [bg, fg, label] = DEP_TONE[x.outcome] || DEP_TONE.unreadable; const choices = (x.choices && x.choices.length) ? x.choices : (dep.accounts || []).map(a => ({ id: a.id, label: `${a.company} · ${a.name} ····${a.last4}` })); return (
    <div key={key} style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', padding: '9px 0', borderTop: '1px solid #f1f5f9' }}>
      <span style={{ display: 'inline-block', padding: '2px 9px', borderRadius: '999px', fontSize: '11px', fontWeight: '700', background: bg, color: fg, whiteSpace: 'nowrap' }}>{label}</span>
      <div style={{ flex: '1 1 320px', minWidth: 0, fontSize: '13px', color: '#334155' }}>
        <b>{x.account || [x.bank_name, x.account_label].filter(Boolean).join(' · ') || x.filename || 'Picture'}</b>{x.as_of ? ` · ${fmtDate(x.as_of)}` : ''}{x.found != null ? ` · ${x.found} deposit${x.found === 1 ? '' : 's'}` : (x.deposits_seen != null ? ` · ${x.deposits_seen} deposit${x.deposits_seen === 1 ? '' : 's'} seen` : '')}{x.posted ? ` · ${x.posted} posted` : ''}{x.unmatched ? ` · ${x.unmatched} to assign` : ''}
        <div style={{ fontSize: '12px', color: '#64748b' }}>{x.message || x.problem || ''}{x.how ? ` (account told by ${x.how})` : ''}</div>
      </div>
      {x.id && <button style={{ ...s.actionBtn, color: '#2563eb' }} onClick={() => openDepFile(x.id)}>Picture</button>}
      {x.outcome === 'needs_account' && x.id && (<>
        <select style={{ ...s.filterSelect, minWidth: '220px' }} value={depPick[x.id] || ''} onChange={e => { const v = e.target.value; if (v === '__new') { setNewAcct({ organization_id: x.facility_id || '', name: x.account_label || '', last4: (x.account_last4 || '').replace(/\D/g, '').slice(-4), bank_name: x.bank_name || '' }); setAcctFor(x.id); setShowAcct(true); setDepPick({ ...depPick, [x.id]: '' }); } else setDepPick({ ...depPick, [x.id]: v }); }}>
          <option value="">— Which account? —</option>{choices.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}<option value="__new">+ Add this account…</option></select>
        <button style={{ ...s.receiveBtn, opacity: depBusy ? 0.6 : 1 }} disabled={depBusy} onClick={() => setDepAccount(x.id)}>Save</button></>)}
    </div>); };
  const openEdit = (rec) => {
    setEditRecord(rec);
    setEditForm({ date_received: rec.date_received ? rec.date_received.split('T')[0] : '', customer: rec.customer || '', amount: rec.amount || '', payment_method: rec.payment_method || 'EFT', notes: rec.notes || '' });
  };

  const handleSaveEdit = async () => {
    await fetch(`${API}/api/collections/${editRecord.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
      body: JSON.stringify({ date_received: editForm.date_received, customer: editForm.customer, amount: parseFloat(editForm.amount), payment_method: editForm.payment_method, notes: editForm.notes }),
    });
    setEditRecord(null);
    await loadData();
  };

  const handleDeleteReceived = async () => {
    if (!window.confirm('Delete this payment? Cannot be undone.')) return;
    const dr = await fetch(`${API}/api/collections/${editRecord.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token()}` } });
    const dj = await dr.json().catch(() => ({}));
    if (dr.ok === false || dj.ok === false) { window.alert(dj.error || 'This payment could not be deleted.'); return; }
    setEditRecord(null);
    await loadData();
  };

  // Compute today stats per facility from payments list
  const todayByFacility = facilities.map(f => {
    const rows = payments.filter(p => p.facility_id === f.id && isToday(p.date_received || p.created_at));
    return { ...f, total: rows.reduce((s, p) => s + parseFloat(p.amount || 0), 0), count: rows.length };
  });
  const todayGrandTotal = todayByFacility.reduce((s, f) => s + f.total, 0);
  const todayGrandCount = todayByFacility.reduce((s, f) => s + f.count, 0);

  // Pending per facility
  const pendingByFacility = facilities.map(f => ({
    ...f,
    pendingTotal: pending.filter(p => p.facility_id === f.id).reduce((s, p) => s + parseFloat(p.expected_amount || 0), 0),
    pendingCount: pending.filter(p => p.facility_id === f.id).length,
  }));
  const totalPending = pending.reduce((s, p) => s + parseFloat(p.expected_amount || 0), 0);

  // Merge facility summary with pending
  const monthFor = (fid) => {
    const row = monthData.find(m => m.month === selectedMonth && m.facility_id === fid);
    return row ? { total: parseFloat(row.total || 0), payments: row.payments || 0 } : { total: 0, payments: 0 };
  };
  const facilityRows = facilities.map(f => {
    const sum = facilitySummary.find(fs => fs.facility_id === f.id) || {};
    const monthSel = monthFor(f.id);
    // monthTotal / monthPayments feed the selected-month column
    const monthTotal = monthSel.total;
    const pend = pendingByFacility.find(pb => pb.id === f.id) || {};
    const todayFac = todayByFacility.find(tf => tf.id === f.id) || {};
    return {
      ...f,
      today: todayFac.total || 0,
      monthTotal: monthTotal,
      mtd: parseFloat(sum.mtd || 0),
      ytd: parseFloat(sum.ytd || 0),
      pending: pend.pendingTotal || 0,
      pendingCount: pend.pendingCount || 0,
    };
  });

  const grandMTD = facilityRows.reduce((s, f) => s + f.mtd, 0);
  const grandMonth = facilityRows.reduce((s, f) => s + (f.monthTotal || 0), 0);
  const grandYTD = facilityRows.reduce((s, f) => s + f.ytd, 0);
  const grandPending = facilityRows.reduce((s, f) => s + f.pending, 0);

  const filteredReceived = payments.filter(p => {
    const matchFacility = filterFacility === 'all' || p.facility_id === filterFacility;
    const matchSearch = !search || (p.customer || '').toLowerCase().includes(search.toLowerCase()) || (p.facility_name || '').toLowerCase().includes(search.toLowerCase());
    return matchFacility && matchSearch;
  });

  return (
    <div style={s.page}>
      <div style={s.pageTitle}>Collections</div>
      <div style={s.pageSub}>Track received and pending insurance payments across all facilities</div>

      {/* TODAY BANNER */}
      <div style={s.todayBanner}>
        <div style={s.todayTop}>
          <div>
            <div style={s.todayLabel}>Collected Today</div>
            <div style={s.todayTotal}>{fmtMoney(todayGrandTotal)}</div>
            <div style={s.todayCount}>{todayGrandCount} payment{todayGrandCount !== 1 ? 's' : ''} received today</div>
          </div>
          <div style={{ textAlign: 'right', opacity: 0.8 }}>
            <div style={{ fontSize: '12px', fontWeight: '700', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Pending (All Facilities)</div>
            <div style={{ fontSize: '28px', fontWeight: '800', color: '#fde68a' }}>{fmtMoney(totalPending)}</div>
            <div style={{ fontSize: '12px' }}>{pending.length} payment{pending.length !== 1 ? 's' : ''} outstanding</div>
          </div>
        </div>
        <div style={s.todayFacilities}>
          {todayByFacility.map(f => (
            <div key={f.id} style={s.todayFacCard}>
              <div style={s.todayFacName}>{f.short}</div>
              <div style={s.todayFacAmt}>{fmtMoney(f.total)}</div>
              <div style={s.todayFacCount}>{f.count} payment{f.count !== 1 ? 's' : ''}</div>
            </div>
          ))}
        </div>
      </div>

      {/* FACILITY SUMMARY GRID */}
      <div style={s.summaryGrid}>
        {/* MTD + YTD table */}
        <div style={s.summaryCard}>
          <div style={s.summaryCardTitle}>
            <span>Collections by Facility</span>
            <select
              value={selectedMonth || ''}
              onChange={e => setSelectedMonth(e.target.value)}
              style={{ fontSize: '12px', padding: '4px 8px', border: '1px solid #e2e8f0', borderRadius: '6px', background: '#fff', color: '#334155', cursor: 'pointer', fontWeight: '600' }}
            >
              {monthList.map(m => <option key={m} value={m}>{fmtMonthLabel(m)}</option>)}
            </select>
          </div>
          <table style={s.summaryTable}>
            <thead>
              <tr>
                <th style={s.summaryThLeft}>Facility</th>
                <th style={s.summaryTh}>Today</th>
                <th style={s.summaryTh}>{selectedMonth ? fmtMonthLabel(selectedMonth) : 'MTD'}</th>
                <th style={s.summaryTh}>YTD</th>
              </tr>
            </thead>
            <tbody>
              {facilityRows.map(f => (
                <tr key={f.id}>
                  <td style={s.summaryTdLeft}>{f.name}</td>
                  <td style={s.summaryTdGreen}>{fmtMoney(f.today, true)}</td>
                  <td style={s.summaryTdGreen}>{fmtMoney(f.monthTotal, true)}</td>
                  <td style={s.summaryTdGreen}>{fmtMoney(f.ytd, true)}</td>
                </tr>
              ))}
              <tr style={s.totalRow}>
                <td style={s.totalTdLeft}>Total</td>
                <td style={{ ...s.totalTd, color: '#15803d' }}>{fmtMoney(todayGrandTotal, true)}</td>
                <td style={{ ...s.totalTd, color: '#15803d' }}>{fmtMoney(grandMonth, true)}</td>
                <td style={{ ...s.totalTd, color: '#15803d' }}>{fmtMoney(grandYTD, true)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Pending by facility */}
        <div style={s.summaryCard}>
          <div style={s.summaryCardTitle}>
            <span>Pending by Facility</span>
            <span style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '400' }}>{pending.length} total outstanding</span>
          </div>
          <table style={s.summaryTable}>
            <thead>
              <tr>
                <th style={s.summaryThLeft}>Facility</th>
                <th style={s.summaryTh}>Payments</th>
                <th style={s.summaryTh}>Expected</th>
              </tr>
            </thead>
            <tbody>
              {facilityRows.map(f => (
                <tr key={f.id}>
                  <td style={s.summaryTdLeft}>{f.name}</td>
                  <td style={s.summaryTd}>{f.pendingCount || '—'}</td>
                  <td style={s.summaryTdYellow}>{f.pending > 0 ? fmtMoney(f.pending, true) : '—'}</td>
                </tr>
              ))}
              <tr style={s.totalRow}>
                <td style={s.totalTdLeft}>Total</td>
                <td style={s.totalTd}>{pending.length}</td>
                <td style={{ ...s.totalTd, color: '#d97706' }}>{fmtMoney(grandPending, true)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* TABS */}
      <div style={s.tabRow}>
        <button style={s.tab(activeTab === 'received')} onClick={() => { setActiveTab('received'); setSubmitMsg(null); }}>
          Received Payments
        </button>
        <button style={s.tab(activeTab === 'pending')} onClick={() => { setActiveTab('pending'); setSubmitMsg(null); }}>
          Pending Payments {pending.length > 0 && `(${pending.length})`}
        </button>
        <button style={s.tab(activeTab === 'deposits')} onClick={() => { setActiveTab('deposits'); setSubmitMsg(null); }}>
          Bank deposits {dep.unmatched.length > 0 && `(${dep.unmatched.length} to assign)`}
        </button>
        <button style={s.tab(activeTab === 'reconciliation')} onClick={() => { setActiveTab('reconciliation'); setSubmitMsg(null); }}>
          Reconciliation
        </button>
      </div>

      {activeTab === 'deposits' && (
        <>
          {dep.can_upload && (
            <div style={s.formCard}>
              <div style={{ display: 'flex', gap: '14px', alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 380px', minWidth: 0 }}>
                  <div style={{ ...s.formTitle, marginBottom: '4px' }}>Upload today's deposits</div>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>Screenshot each account's activity page, or download it from the bank as a PDF, and drop the files here, several at once. TCIO reads the deposits, works out the account, matches each deposit to a pending payment and posts it as received. Anything it cannot place waits below for one click.</div>
                </div>
                <label style={{ ...s.submitBtn, display: 'inline-block', opacity: depBusy ? 0.6 : 1, cursor: depBusy ? 'default' : 'pointer' }}>
                  {depBusy ? 'Reading...' : 'Choose pictures or PDFs'}
                  <input type="file" multiple accept="image/*,.pdf,application/pdf" style={{ display: 'none' }} disabled={depBusy} onChange={e => { uploadDeposits(e.target.files); e.target.value = ''; }} />
                </label>
              </div>
              {(depResults || []).length > 0 && <div style={{ marginTop: '12px' }}>
                <div style={{ fontSize: '12px', fontWeight: '700', color: '#1e293b', marginBottom: '2px' }}>This upload: {depResults.reduce((n, x) => n + (x.posted || 0), 0)} posted, {depResults.reduce((n, x) => n + (x.unmatched || 0), 0)} to assign, from {depResults.length} picture{depResults.length === 1 ? '' : 's'}</div>
                {depResults.map((x, i) => depRow(x, 'r' + i))}</div>}
              {depWaiting.length > 0 && <div style={{ marginTop: '12px' }}>
                <div style={{ fontSize: '12px', fontWeight: '700', color: '#b45309', marginBottom: '2px' }}>Waiting for an account from earlier uploads</div>
                {depWaiting.map((x, i) => depRow(x, 'w' + i))}</div>}
            </div>
          )}
          {dep.unmatched.length > 0 && (
            <div style={s.tableCard}>
              <div style={s.tableHeader}><div style={s.tableTitle}>Deposits to assign <span style={{ fontSize: '12px', color: '#94a3b8', fontWeight: '400', marginLeft: '8px' }}>{dep.unmatched.length} · {fmtMoney(dep.unmatched.reduce((n, d) => n + d.amount, 0))}</span></div></div>
              <table style={s.table}><thead><tr><th style={s.th}>Date</th><th style={s.th}>Account</th><th style={s.th}>Bank description</th><th style={{ ...s.th, textAlign: 'right' }}>Amount</th><th style={s.th}>What is it?</th><th style={s.th}></th></tr></thead>
                <tbody>{dep.unmatched.map(d => { const a = assign[d.id] || {}; const cands = Array.isArray(d.candidates) ? d.candidates : []; const sameAmt = (dep.pending || []).filter(p => Math.abs(p.expected_amount - d.amount) < 0.005 && !cands.some(c => c.id === p.id)); const others = (dep.pending || []).filter(p => Math.abs(p.expected_amount - d.amount) >= 0.005);
                  return (<tr key={d.id}><td style={s.td}>{fmtDate(d.deposit_date)}</td><td style={s.td}>{d.company}<div style={{ fontSize: '11px', color: '#64748b' }}>{d.account_name} ····{d.last4}</div></td><td style={{ ...s.td, fontSize: '12px', maxWidth: '320px' }}>{d.description}{d.is_pending ? <span style={{ marginLeft: 6, padding: '1px 6px', borderRadius: 999, fontSize: 10, fontWeight: 700, background: '#fef3c7', color: '#b45309' }}>pending at the bank</span> : null}</td><td style={s.tdGreen}>{fmtMoney(d.amount)}</td>
                    <td style={s.td}>
                      <select style={{ ...s.filterSelect, maxWidth: '360px' }} value={a.pending_payment_id ? 'p:' + a.pending_payment_id : (a.kind ? 'k:' + a.kind : '')} onChange={e => { const v = e.target.value; setAssign({ ...assign, [d.id]: v.startsWith('p:') ? { pending_payment_id: v.slice(2) } : { kind: v.slice(2) } }); }}>
                        <option value="">— Choose —</option>
                        {cands.length > 0 && <optgroup label="Likely matches">{cands.map(c => <option key={c.id} value={'p:' + c.id}>{c.payer_name} · {fmtMoney(c.expected_amount)}{c.check_number ? ' · ' + c.check_number : ''} ({c.why})</option>)}</optgroup>}
                        {sameAmt.length > 0 && <optgroup label="Pending payments for this amount">{sameAmt.map(p => <option key={p.id} value={'p:' + p.id}>{p.facility_name} · {p.payer_name} · {fmtMoney(p.expected_amount)}{p.check_number ? ' · ' + p.check_number : ''}</option>)}</optgroup>}
                        <optgroup label="Not a pending payment"><option value="k:insurance_new">Insurance payment with no pending entry</option><option value="k:transfer">Transfer between our accounts</option><option value="k:patient">Patient payment</option><option value="k:refund">Refund or credit to us</option><option value="k:other">Something else (note)</option><option value="k:ignore">Set aside</option></optgroup>
                        {others.length > 0 && <optgroup label="Other pending payments (different amount)">{others.slice(0, 60).map(p => <option key={p.id} value={'p:' + p.id}>{p.facility_name} · {p.payer_name} · {fmtMoney(p.expected_amount)}{p.check_number ? ' · ' + p.check_number : ''}</option>)}</optgroup>}
                      </select>
                      {a.pending_payment_id && others.some(p => p.id === a.pending_payment_id) && <label style={{ display: 'block', fontSize: '11px', color: '#b45309', marginTop: '4px' }}><input type="checkbox" checked={!!a.accept} onChange={e => setAssign({ ...assign, [d.id]: { ...a, accept: e.target.checked } })} /> post the deposit amount anyway (the difference stays on the claim in RCM)</label>}
                      {a.kind === 'insurance_new' && <div style={{ display: 'flex', gap: '6px', marginTop: '4px' }}><input style={{ ...s.input, width: '180px' }} placeholder="Plan name" list="payer-suggestions" value={a.payer_name || ''} onChange={e => setAssign({ ...assign, [d.id]: { ...a, payer_name: e.target.value } })} /><select style={s.filterSelect} value={a.facility_id || d.facility_id} onChange={e => setAssign({ ...assign, [d.id]: { ...a, facility_id: e.target.value } })}>{(dep.companies || []).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select></div>}
                      {(a.kind === 'other' || a.kind === 'transfer' || a.kind === 'patient' || a.kind === 'refund') && <input style={{ ...s.input, marginTop: '4px' }} placeholder={a.kind === 'other' ? 'What is it? (required)' : 'Note (optional)'} value={a.note || ''} onChange={e => setAssign({ ...assign, [d.id]: { ...a, note: e.target.value } })} />}
                    </td>
                    <td style={s.td}><button style={{ ...s.receiveBtn, opacity: depBusy ? 0.6 : 1 }} disabled={depBusy} onClick={() => assignDeposit(d)}>Save</button></td></tr>); })}</tbody></table>
            </div>
          )}
          <div style={s.tableCard}>
            <div style={s.tableHeader}><div style={s.tableTitle}>Deposits read, last 14 days</div>{dep.can_upload && <button style={s.clearBtn} onClick={() => setShowAcct(!showAcct)}>{showAcct ? 'Close' : 'Add a bank account'}</button>}</div>
            {showAcct && <div style={{ padding: '12px 18px', borderBottom: '1px solid #f1f5f9', display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
              <select style={s.filterSelect} value={newAcct.organization_id} onChange={e => setNewAcct({ ...newAcct, organization_id: e.target.value })}><option value="">— Company —</option>{(dep.companies || []).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select>
              <input style={{ ...s.input, width: '160px' }} placeholder="Bank name" value={newAcct.bank_name} onChange={e => setNewAcct({ ...newAcct, bank_name: e.target.value })} />
              <input style={{ ...s.input, width: '160px' }} placeholder="Account name" value={newAcct.name} onChange={e => setNewAcct({ ...newAcct, name: e.target.value })} />
              <input style={{ ...s.input, width: '110px' }} placeholder="Last 4 only" maxLength={4} value={newAcct.last4} onChange={e => setNewAcct({ ...newAcct, last4: e.target.value.replace(/\D/g, '').slice(0, 4) })} />
              <button style={s.submitBtn} disabled={depBusy} onClick={addAccount}>Save</button><span style={{ fontSize: '11px', color: '#64748b' }}>Only the last four digits are ever stored.</span></div>}
            {dep.strip.length === 0 ? <div style={s.emptyRow}>No deposits read yet. Upload the first screenshots above.</div> : (
              <table style={s.table}><thead><tr><th style={s.th}>Day</th><th style={s.th}>Company</th><th style={s.th}>Account</th><th style={{ ...s.th, textAlign: 'right' }}>Deposits</th><th style={s.th}>Posted</th><th style={s.th}>To assign</th></tr></thead>
                <tbody>{dep.strip.map((x, i) => <tr key={i}><td style={s.td}>{fmtDate(x.day)}</td><td style={s.td}>{x.company}</td><td style={s.td}>{x.account_name} ····{x.last4}</td><td style={s.tdGreen}>{fmtMoney(x.total)}</td><td style={s.td}>{x.posted} of {x.n}</td><td style={{ ...s.td, color: x.unmatched ? '#b45309' : '#94a3b8', fontWeight: x.unmatched ? '700' : '400' }}>{x.unmatched || '—'}</td></tr>)}</tbody></table>)}
          </div>
          {(dep.uploads || []).length > 0 && <div style={s.tableCard}>
            <div style={s.tableHeader}><div style={s.tableTitle}>Pictures uploaded</div></div>
            <div style={{ padding: '0 18px 8px' }}>{dep.uploads.filter(x => x.outcome !== 'needs_account').slice(0, 30).map((x, i) => depRow({ ...x, account: x.account_name ? `${x.company} · ${x.account_name} ····${x.last4}` : null, found: x.deposits_found, posted: x.deposits_posted, unmatched: x.deposits_unmatched, message: `${x.uploaded_by_name ? x.uploaded_by_name + ' · ' : ''}${new Date(x.uploaded_at).toLocaleString('en-US')}${x.problem ? ' · ' + x.problem : ''}` }, 'u' + i))}</div>
          </div>}
        </>
      )}

      {activeTab === 'reconciliation' && <Reconciliation />}

      {/* ── RECEIVED TAB ── */}
      {activeTab === 'received' && (
        <>
          <div style={s.formCard}>
            <div style={s.formTitle}>Log Received Payment</div>
            <div style={s.formGrid4}>
              <div>
                <label style={s.label}>Date Received *</label>
                <input type="date" style={s.input} value={receivedForm.date_received}
                  onChange={e => setReceivedForm({ ...receivedForm, date_received: e.target.value })} />
              </div>
              <div>
                <label style={s.label}>Facility *</label>
                <select style={s.select} value={receivedForm.facility_id}
                  onChange={e => setReceivedForm({ ...receivedForm, facility_id: e.target.value })}>
                  <option value="">— Select facility —</option>
                  {facilities.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                </select>
              </div>
              <div>
                <label style={s.label}>Payer / Insurance Co. *</label>
                <input style={s.input} placeholder="e.g. Anthem Blue Cross"
                  value={receivedForm.customer}
                  onChange={e => setReceivedForm({ ...receivedForm, customer: e.target.value })} list="payer-suggestions" />
                <datalist id="payer-suggestions">
                  {payerList.map(p => <option key={p} value={p} />)}
                </datalist>
              </div>
              <div>
                <label style={s.label}>Amount *</label>
                <input style={s.input} type="number" step="0.01" placeholder="0.00"
                  value={receivedForm.amount}
                  onChange={e => setReceivedForm({ ...receivedForm, amount: e.target.value })} />
              </div>
            </div>
            <div style={s.formGrid4}>
              <div>
                <label style={s.label}>Payment Method</label>
                <select style={s.select} value={receivedForm.payment_method}
                  onChange={e => setReceivedForm({ ...receivedForm, payment_method: e.target.value })}>
                  {PAYMENT_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div>
                <label style={s.label}>Check # / EFT Ref</label>
                <input style={s.input} placeholder="Reference number"
                  value={receivedForm.check_number}
                  onChange={e => setReceivedForm({ ...receivedForm, check_number: e.target.value })} />
              </div>
              <div>
                <label style={s.label}>Patient Name (optional)</label>
                <input style={s.input} placeholder="If single patient payment"
                  value={receivedForm.patient_name}
                  onChange={e => setReceivedForm({ ...receivedForm, patient_name: e.target.value })} />
              </div>
              <div>
                <label style={s.label}>Notes</label>
                <input style={s.input} placeholder="Any additional notes"
                  value={receivedForm.notes}
                  onChange={e => setReceivedForm({ ...receivedForm, notes: e.target.value })} />
              </div>
            </div>
            <div style={s.formActions}>
              <button style={{ ...s.submitBtn, opacity: submitting ? 0.6 : 1 }} onClick={handleSubmitReceived} disabled={submitting}>
                {submitting ? 'Saving...' : 'Log Payment'}
              </button>
              <button style={s.clearBtn} onClick={() => { setReceivedForm({ ...emptyReceived }); setSubmitMsg(null); }}>Clear</button>
              {submitMsg && <span style={submitMsg.type === 'success' ? s.successMsg : s.errorMsg}>{submitMsg.text}</span>}
            </div>
          </div>

          <div style={s.tableCard}>
            <div style={s.tableHeader}>
              <div style={s.tableTitle}>
                Recent Payments
                <span style={{ fontSize: '12px', color: '#94a3b8', fontWeight: '400', marginLeft: '8px' }}>
                  {filteredReceived.length} records · {fmtMoney(filteredReceived.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0))}
                </span>
              </div>
              <div style={s.filterRow}>
                <input style={s.searchInput} placeholder="Search payer..." value={search} onChange={e => setSearch(e.target.value)} />
                <select style={s.filterSelect} value={filterFacility} onChange={e => setFilterFacility(e.target.value)}>
                  <option value="all">All Facilities</option>
                  {facilities.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                </select>
              </div>
            </div>
            {loading ? <div style={s.emptyRow}>Loading...</div> : filteredReceived.length === 0 ? (
              <div style={s.emptyRow}>No payments found.</div>
            ) : (
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>Date</th>
                    <th style={s.th}>Facility</th>
                    <th style={s.th}>Payer</th>
                    <th style={s.th}>Patients</th>
                    <th style={s.th}>Method</th>
                    <th style={{ ...s.th, textAlign: 'right' }}>Amount</th>
                    <th style={s.th}></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredReceived.map((p, i) => (
                    <tr key={p.id || i}
                      onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                      onMouseLeave={e => e.currentTarget.style.background = ''}>
                      <td style={s.td}>
                        {fmtDate(p.date_received)}
                        {isToday(p.date_received || p.created_at) && <span style={s.todayTag}>TODAY</span>}
                      </td>
                      <td style={s.td}>{p.facility_name || '—'}</td>
                      <td style={s.td}>{p.customer || '—'}</td>
                      <td style={{ ...s.td, fontSize: '12px', color: '#64748b', maxWidth: '340px' }}>{p.patient_names || '—'}</td>
                      <td style={s.td}>{p.payment_method || '—'}</td>
                      <td style={s.tdGreen}>{fmtMoney(p.amount)}</td>
                      <td style={s.td}>
                        <button style={{ ...s.actionBtn, color: '#2563eb' }} onClick={() => openEdit(p)}>Edit</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {/* ── PENDING TAB ── */}
      {activeTab === 'pending' && (
        <>
          {eob.can_upload && (
            <div style={s.formCard}>
              <div style={{ display: 'flex', gap: '14px', alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 380px', minWidth: 0 }}>
                  <div style={{ ...s.formTitle, marginBottom: '4px' }}>Upload remittances</div>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>Download the day's check details from the clearinghouse remittance viewer as PDFs and add them here, several at once. Each payment becomes a pending payment below. A remittance that paid nothing goes to RCM only.</div>
                </div>
                <label style={{ ...s.submitBtn, display: 'inline-block', opacity: eobBusy ? 0.6 : 1, cursor: eobBusy ? 'default' : 'pointer' }}>
                  {eobBusy ? 'Reading...' : 'Choose files'}
                  <input type="file" multiple accept="application/pdf,.pdf" style={{ display: 'none' }} disabled={eobBusy} onChange={e => { uploadEobs(e.target.files); e.target.value = ''; }} />
                </label>
              </div>
              {(eobResults || []).length > 0 && <div style={{ marginTop: '12px' }}>
                <div style={{ fontSize: '12px', fontWeight: '700', color: '#1e293b', marginBottom: '2px' }}>This upload: {eobResults.filter(x => x.outcome === 'pending_created').length} pending payment{eobResults.filter(x => x.outcome === 'pending_created').length === 1 ? '' : 's'} created from {eobResults.length} payment{eobResults.length === 1 ? '' : 's'} read</div>
                {eobResults.map((x, i) => eobRow(x, 'r' + i))}</div>}
              {eobWaiting.length > 0 && <div style={{ marginTop: '12px' }}>
                <div style={{ fontSize: '12px', fontWeight: '700', color: '#b45309', marginBottom: '2px' }}>Waiting for a facility from earlier uploads</div>
                {eobWaiting.map((x, i) => eobRow(x, 'w' + i))}</div>}
            </div>
          )}
          <div style={s.formCard}>
            <div style={s.formTitle}>Log Pending Payment</div>
            <div style={s.formGrid4}>
              <div>
                <label style={s.label}>Facility *</label>
                <select style={s.select} value={pendingForm.facility_id}
                  onChange={e => setPendingForm({ ...pendingForm, facility_id: e.target.value })}>
                  <option value="">— Select facility —</option>
                  {facilities.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                </select>
              </div>
              <div>
                <label style={s.label}>Payer / Insurance Co. *</label>
                <input style={s.input} placeholder="e.g. Health Net"
                  value={pendingForm.payer_name}
                  onChange={e => setPendingForm({ ...pendingForm, payer_name: e.target.value })} />
              </div>
              <div>
                <label style={s.label}>Expected Amount *</label>
                <input style={s.input} type="number" step="0.01" placeholder="0.00"
                  value={pendingForm.expected_amount}
                  onChange={e => setPendingForm({ ...pendingForm, expected_amount: e.target.value })} />
              </div>
              <div>
                <label style={s.label}>Payment Method</label>
                <select style={s.select} value={pendingForm.payment_method}
                  onChange={e => setPendingForm({ ...pendingForm, payment_method: e.target.value })}>
                  {PAYMENT_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
            </div>
            <div style={s.formGrid4}>
              <div>
                <label style={s.label}>Issue Date (from EOB)</label>
                <input type="date" style={s.input} value={pendingForm.issue_date}
                  onChange={e => setPendingForm({ ...pendingForm, issue_date: e.target.value })} />
              </div>
              <div>
                <label style={s.label}>Expected Receive Date</label>
                <input type="date" style={s.input} value={pendingForm.expected_date}
                  onChange={e => setPendingForm({ ...pendingForm, expected_date: e.target.value })} />
              </div>
              <div>
                <label style={s.label}>Check # / EFT Ref</label>
                <input style={s.input} placeholder="Reference number"
                  value={pendingForm.check_number}
                  onChange={e => setPendingForm({ ...pendingForm, check_number: e.target.value })} />
              </div>
              <div>
                <label style={s.label}>Notes</label>
                <input style={s.input} placeholder="Any notes"
                  value={pendingForm.notes}
                  onChange={e => setPendingForm({ ...pendingForm, notes: e.target.value })} />
              </div>
            </div>
            <div style={s.formActions}>
              <button style={{ ...s.submitBtnYellow, opacity: submitting ? 0.6 : 1 }} onClick={handleSubmitPending} disabled={submitting}>
                {submitting ? 'Saving...' : 'Log Pending Payment'}
              </button>
              <button style={s.clearBtn} onClick={() => { setPendingForm({ ...emptyPending }); setSubmitMsg(null); }}>Clear</button>
              {submitMsg && <span style={submitMsg.type === 'success' ? s.successMsg : s.errorMsg}>{submitMsg.text}</span>}
            </div>
          </div>

          <div style={s.tableCard}>
            <div style={s.tableHeader}>
              <div style={s.tableTitle}>
                Outstanding Payments
                <span style={{ fontSize: '12px', color: '#94a3b8', fontWeight: '400', marginLeft: '8px' }}>
                  {pending.length} pending · {fmtMoney(totalPending)} expected
                </span>
              </div>
            </div>
            {loading ? <div style={s.emptyRow}>Loading...</div> : pending.length === 0 ? (
              <div style={s.emptyRow}>No pending payments. Log one above when you see a payment issued on Availity.</div>
            ) : (
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>Facility</th>
                    <th style={s.th}>Payer</th>
                    <th style={s.th}>Method</th>
                    <th style={s.th}>Check # / Ref</th>
                    <th style={s.th}>Issue Date</th>
                    <th style={s.th}>Expected Date</th>
                    <th style={{ ...s.th, textAlign: 'right' }}>Amount</th>
                    <th style={s.th}>Notes</th>
                    <th style={s.th}></th>
                  </tr>
                </thead>
                <tbody>
                  {pending.map((p, i) => (
                    <tr key={p.id || i}
                      onMouseEnter={e => e.currentTarget.style.background = '#fffbeb'}
                      onMouseLeave={e => e.currentTarget.style.background = ''}>
                      <td style={s.td}>{p.facility_name || '—'}</td>
                      <td style={s.td}>{p.payer_name}</td>
                      <td style={s.td}>{p.payment_method || '—'}</td>
                      <td style={s.td}>{p.check_number || '—'}</td>
                      <td style={s.td}>{fmtDate(p.issue_date)}</td>
                      <td style={s.td}>
                        {fmtDate(p.expected_date)}
                        {isOverdue(p.expected_date) && <span style={s.overdueTag}>OVERDUE</span>}
                      </td>
                      <td style={s.tdYellow}>{fmtMoney(p.expected_amount)}</td>
                      <td style={s.td}>{p.notes || '—'}</td>
                      <td style={s.td}>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button style={s.receiveBtn} onClick={() => { setReceiveModal(p); setReceiveAmount(p.expected_amount); setReceiveDate(todayStr()); }}>
                            Mark Received
                          </button>
                          {eob.links && eob.links[p.id] && <button style={{ ...s.actionBtn, color: '#2563eb' }} title="Open the remittance this came from" onClick={() => openEob(eob.links[p.id])}>EOB</button>}
                          <button style={s.actionBtn} onClick={() => { setEditPending(p); setEditPendingForm({ payer_name: p.payer_name || '', expected_amount: p.expected_amount || '', issue_date: p.issue_date ? String(p.issue_date).slice(0,10) : '', expected_date: p.expected_date ? String(p.expected_date).slice(0,10) : '', check_number: p.check_number || '', payment_method: p.payment_method || 'Check', notes: p.notes || '' }); }}>Edit</button>
                          <button style={{ ...s.actionBtn, color: '#dc2626' }} onClick={() => handleDeletePending(p.id)}>✕</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {editPending && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setEditPending(null)}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 460, maxWidth: '92vw' }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 12 }}>Edit Pending Payment</div>
            {[['payer_name','Payer','text'],['expected_amount','Expected Amount','number'],['check_number','Check / EFT #','text'],['issue_date','Issue Date','date'],['expected_date','Expected Date','date'],['notes','Notes','text']].map(([k, l, t]) => (
              <div key={k} style={{ marginBottom: 10 }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', display: 'block', marginBottom: 3 }}>{l}</label>
                <input type={t} value={editPendingForm[k] ?? ''} onChange={e => setEditPendingForm({ ...editPendingForm, [k]: e.target.value })}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, boxSizing: 'border-box' }} />
              </div>
            ))}
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', display: 'block', marginBottom: 3 }}>Method</label>
              <select value={editPendingForm.payment_method || 'Check'} onChange={e => setEditPendingForm({ ...editPendingForm, payment_method: e.target.value })}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13 }}>
                <option>Check</option><option>EFT</option><option>ACH</option><option>Wire</option><option>Other</option>
              </select>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setEditPending(null)} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#fff', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
              <button onClick={savePendingEdit} disabled={editPendingBusy}
                style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#16a34a', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer', opacity: editPendingBusy ? 0.6 : 1 }}>
                {editPendingBusy ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Mark Received Modal */}
      {receiveModal && (
        <div style={s.overlay} onClick={e => e.target === e.currentTarget && setReceiveModal(null)}>
          <div style={s.modal}>
            <div style={s.modalTitle}>Mark as Received</div>
            <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '16px' }}>
              Confirming receipt of <strong>{receiveModal.payer_name}</strong> — {receiveModal.facility_name}. Adjust amount if the actual deposit differs from expected.
            </p>
            <div style={s.modalGrid}>
              <div>
                <label style={s.label}>Date Received</label>
                <input type="date" style={s.input} value={receiveDate} onChange={e => setReceiveDate(e.target.value)} />
              </div>
              <div>
                <label style={s.label}>Actual Amount</label>
                <input type="number" step="0.01" style={s.input} value={receiveAmount} onChange={e => setReceiveAmount(e.target.value)} />
              </div>
              <div>
                <label style={s.label}>Into which bank account</label>
                <select style={s.input} value={receiveAccount} onChange={e => setReceiveAccount(e.target.value)}>
                  <option value="">Not sure yet (the bank page will tell us)</option>
                  {(dep.accounts || []).filter(a => !receiveModal.facility_name || !a.company || a.company === receiveModal.facility_name || (receiveModal.facility_name || '').startsWith(a.company)).map(a => <option key={a.id} value={a.id}>{a.company} · {a.name} ····{a.last4}</option>)}
                </select>
              </div>
            </div>
            <div style={s.modalActions}>
              <button style={s.cancelBtn} onClick={() => setReceiveModal(null)}>Cancel</button>
              <button style={{ ...s.saveBtn, background: '#15803d' }} onClick={handleReceive}>Confirm Receipt</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Received Modal */}
      {editRecord && (
        <div style={s.overlay} onClick={e => e.target === e.currentTarget && setEditRecord(null)}>
          <div style={s.modal}>
            <div style={s.modalTitle}>Edit Payment</div>
            <div style={s.modalGrid}>
              <div>
                <label style={s.label}>Date Received</label>
                <input type="date" style={s.input} value={editForm.date_received} onChange={e => setEditForm({ ...editForm, date_received: e.target.value })} />
              </div>
              <div>
                <label style={s.label}>Amount</label>
                <input type="number" step="0.01" style={s.input} value={editForm.amount} onChange={e => setEditForm({ ...editForm, amount: e.target.value })} />
              </div>
              <div>
                <label style={s.label}>Payer</label>
                <input style={s.input} value={editForm.customer} onChange={e => setEditForm({ ...editForm, customer: e.target.value })} />
              </div>
              <div>
                <label style={s.label}>Payment Method</label>
                <select style={s.select} value={editForm.payment_method} onChange={e => setEditForm({ ...editForm, payment_method: e.target.value })}>
                  {PAYMENT_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={s.label}>Notes</label>
                <textarea style={{ ...s.input, minHeight: 56, resize: 'vertical' }} value={editForm.notes} onChange={e => setEditForm({ ...editForm, notes: e.target.value })} />
              </div>
            </div>
            <div style={s.modalActions}>
              <button style={s.deleteBtn} onClick={handleDeleteReceived}>Delete</button>
              <button style={s.cancelBtn} onClick={() => setEditRecord(null)}>Cancel</button>
              <button style={s.saveBtn} onClick={handleSaveEdit}>Save Changes</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}